<template>
  <div>
    <Navbar>
      <div class="card">
        <div class="card-body">
          <!-- LISTADO -->
          <template v-if="view === 'list'">
            <div class="row mb-3">
              <div class="col-md-6">
                <h4 class="mb-0">Conciliación de inventario</h4>
                <p class="text-muted small mb-0">
                  Compara inventario físico vs sistema y aplica ajustes con trazabilidad.
                </p>
              </div>
              <div class="col-md-6 text-right">
                <button class="btn btn-primary" @click="openNewModal">
                  <i class="fa fa-plus"></i> Nueva conciliación
                </button>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-md-3">
                <label class="small font-weight-bold">Estado</label>
                <select class="form-control" v-model="statusFilter" @change="loadList">
                  <option value="">Todos</option>
                  <option value="draft">Borrador</option>
                  <option value="closed">Cerrada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
            </div>
            <div v-if="loading" class="text-center">
              <HorizontalLoader />
            </div>
            <div v-else>
              <table class="table">
                <thead class="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Creada</th>
                    <th>Cerrada</th>
                    <th>Estado</th>
                    <th>Batch</th>
                    <th>Notas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in reconciliations" :key="r.Id">
                    <td>{{ r.Id }}</td>
                    <td>{{ formatDate(r.CreatedAt) }}</td>
                    <td>{{ r.ClosedAt ? formatDate(r.ClosedAt) : "-" }}</td>
                    <td>
                      <span :class="statusBadge(r.Status)">{{ statusLabel(r.Status) }}</span>
                    </td>
                    <td>{{ r.BatchId || "-" }}</td>
                    <td>{{ r.Notes || "-" }}</td>
                    <td>
                      <button class="btn btn-sm btn-outline-primary" @click="openDetail(r.Id)">
                        <i class="fa fa-eye"></i> Abrir
                      </button>
                    </td>
                  </tr>
                  <tr v-if="reconciliations.length === 0">
                    <td colspan="7" class="text-center text-muted">Sin conciliaciones</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- DETALLE -->
          <template v-else-if="view === 'detail'">
            <div class="row mb-3">
              <div class="col-md-8">
                <button class="btn btn-link p-0" @click="backToList">
                  <i class="fa fa-arrow-left"></i> Volver al listado
                </button>
                <h4 class="mt-2 mb-0">
                  Conciliación #{{ current.Id }}
                  <span :class="statusBadge(current.Status)">{{ statusLabel(current.Status) }}</span>
                </h4>
                <p class="text-muted small">
                  Creada {{ formatDate(current.CreatedAt) }}
                  <span v-if="current.ClosedAt"> · Cerrada {{ formatDate(current.ClosedAt) }}</span>
                  <span v-if="current.BatchId"> · Batch {{ current.BatchId }}</span>
                </p>
              </div>
              <div class="col-md-4 text-right">
                <template v-if="current.Status === 'draft'">
                  <button
                    class="btn btn-success mr-2"
                    :disabled="saving"
                    @click="saveDraft"
                  >
                    <i class="fa fa-save"></i> Guardar avance
                  </button>
                  <button
                    class="btn btn-primary mr-2"
                    :disabled="saving"
                    @click="closeRecon"
                  >
                    <i class="fa fa-check"></i> Cerrar conciliación
                  </button>
                  <button
                    class="btn btn-outline-danger"
                    :disabled="saving"
                    @click="cancelRecon"
                  >
                    <i class="fa fa-ban"></i> Cancelar
                  </button>
                </template>
                <template v-else>
                  <button class="btn btn-outline-primary" @click="downloadExcel">
                    <i class="fa fa-download"></i> Excel
                  </button>
                </template>
              </div>
            </div>

            <div v-if="loading" class="text-center">
              <HorizontalLoader />
            </div>
            <div v-else>
              <div v-if="current.Status !== 'draft' && report" class="row mb-3">
                <div class="col-md-3">
                  <div class="card bg-danger text-white p-3 text-center">
                    <h6 class="mb-0">Faltantes</h6>
                    <h3 class="mb-0">{{ report.totales.faltantesCount }}</h3>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-success text-white p-3 text-center">
                    <h6 class="mb-0">Sobrantes</h6>
                    <h3 class="mb-0">{{ report.totales.sobrantesCount }}</h3>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-secondary text-white p-3 text-center">
                    <h6 class="mb-0">Sin diferencia</h6>
                    <h3 class="mb-0">{{ report.totales.sinDiferenciaCount }}</h3>
                  </div>
                </div>
              </div>

              <table class="table table-sm">
                <thead class="thead-light">
                  <tr>
                    <th>Producto</th>
                    <th class="text-right">Stock sistema</th>
                    <th class="text-right">Stock físico</th>
                    <th class="text-right">Diferencia</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in tableRows" :key="item.Id">
                    <td>{{ item.Name }}</td>
                    <td class="text-right">{{ item.SystemStock }}</td>
                    <td class="text-right" style="width: 120px">
                      <input
                        v-if="current.Status === 'draft'"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control form-control-sm text-right"
                        v-model="item.PhysicalStockEdit"
                      />
                      <span v-else>{{ item.PhysicalStock !== null ? item.PhysicalStock : "-" }}</span>
                    </td>
                    <td class="text-right" :class="diffClass(item)">
                      {{ diffText(item) }}
                    </td>
                    <td>
                      <input
                        v-if="current.Status === 'draft'"
                        type="text"
                        class="form-control form-control-sm"
                        maxlength="255"
                        v-model="item.NotesEdit"
                      />
                      <span v-else>{{ item.Notes || "" }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <!-- MODAL NUEVA CONCILIACIÓN -->
      <div
        class="modal fade"
        id="newReconciliationModal"
        tabindex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-md" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-heading">Nueva conciliación</h5>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="small">Productos a incluir</label>
                <select class="form-control" v-model="newForm.scope">
                  <option value="all">Todos los productos activos</option>
                  <option value="subset">Subconjunto (selección manual)</option>
                </select>
              </div>
              <div v-if="newForm.scope === 'subset'" class="form-group">
                <label class="small">Seleccionar productos</label>
                <multiselect
                  v-model="newForm.products"
                  :placeholder="'-Seleccionar-'"
                  label="Name"
                  track-by="Id"
                  multiple
                  :options="catalog"
                ></multiselect>
              </div>
              <div class="form-group">
                <label class="small">Notas (opcional)</label>
                <textarea
                  class="form-control"
                  maxlength="500"
                  rows="2"
                  v-model="newForm.notes"
                ></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <CloseModalBtn :onClick="closeNewModal" />
              <button class="btn btn-primary" :disabled="creating" @click="createNew">
                <i class="fa fa-plus"></i> Crear
              </button>
            </div>
          </div>
        </div>
      </div>
    </Navbar>
  </div>
</template>

<script>
import Navbar from "../layout/Navbar.vue";
import HorizontalLoader from "../shared/HorizontalLoader.vue";
import CloseModalBtn from "../shared/CloseModalBtn";
import Multiselect from "vue-multiselect";
import {
  createReconciliation,
  listReconciliations,
  getReconciliation,
  updateReconciliationItems,
  closeReconciliation,
  cancelReconciliation,
  getReconciliationReport,
} from "../../services/InventoryReconciliationService";
import { getProductsInventory } from "../../services/ProductsService";
const XLSX = require("xlsx");

export default {
  name: "InventoryReconciliation",
  components: { Navbar, HorizontalLoader, CloseModalBtn, Multiselect },
  data() {
    return {
      view: "list",
      loading: false,
      saving: false,
      creating: false,
      reconciliations: [],
      statusFilter: "",
      catalog: [],
      newForm: { scope: "all", products: [], notes: "" },
      current: { Id: 0, Status: "draft", Items: [] },
      report: null,
      tableRows: [],
    };
  },
  methods: {
    formatDate(s) {
      if (!s) return "";
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString();
    },
    statusLabel(s) {
      if (s === "draft") return "Borrador";
      if (s === "closed") return "Cerrada";
      if (s === "cancelled") return "Cancelada";
      return s;
    },
    statusBadge(s) {
      if (s === "draft") return "badge badge-warning";
      if (s === "closed") return "badge badge-success";
      if (s === "cancelled") return "badge badge-secondary";
      return "badge badge-light";
    },
    diffText(item) {
      const phys =
        item.PhysicalStockEdit !== "" && item.PhysicalStockEdit !== null && item.PhysicalStockEdit !== undefined
          ? Number(item.PhysicalStockEdit)
          : item.PhysicalStock !== null && item.PhysicalStock !== undefined
          ? Number(item.PhysicalStock)
          : null;
      if (phys === null || isNaN(phys)) return "";
      const sys = Number(item.SystemStock);
      const diff = phys - sys;
      return diff > 0 ? `+${diff}` : String(diff);
    },
    diffClass(item) {
      const t = this.diffText(item);
      if (!t) return "";
      if (t.indexOf("+") === 0) return "text-success bold";
      if (t.indexOf("-") === 0) return "text-danger bold";
      return "";
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await listReconciliations(this.statusFilter);
        this.reconciliations = (res && res.data) || [];
      } catch (e) {
        this.$helpers.toast("Error cargando: " + e.message, "error");
      }
      this.loading = false;
    },
    openNewModal() {
      this.newForm = { scope: "all", products: [], notes: "" };
      if (this.catalog.length === 0) this.loadCatalog();
      $("#newReconciliationModal").modal("show");
    },
    closeNewModal() {
      $("#newReconciliationModal").modal("hide");
    },
    async loadCatalog() {
      try {
        const res = await getProductsInventory(this.$store.getters.getCompany, "");
        this.catalog = (res && res.data) || [];
      } catch (e) {
        // best-effort, modal sin catalog igual permite scope=all
      }
    },
    async createNew() {
      this.creating = true;
      try {
        const payload = { Notes: this.newForm.notes };
        if (this.newForm.scope === "subset") {
          payload.ProductIds = (this.newForm.products || []).map((p) => p.Id);
          if (!payload.ProductIds.length) {
            this.$helpers.toast("Selecciona al menos un producto", "warning");
            this.creating = false;
            return;
          }
        }
        const res = await createReconciliation(payload);
        const recon = res && res.data;
        this.closeNewModal();
        if (recon && recon.Id) {
          await this.openDetail(recon.Id);
        } else {
          this.loadList();
        }
      } catch (e) {
        this.$helpers.message("Error", e.message, "error");
      }
      this.creating = false;
    },
    async openDetail(id) {
      this.view = "detail";
      this.loading = true;
      try {
        const res = await getReconciliation(id);
        this.current = (res && res.data) || { Id: id, Status: "draft", Items: [] };
        await this.buildTableRows();
        if (this.current.Status !== "draft") {
          const rep = await getReconciliationReport(id);
          this.report = (rep && rep.data) || null;
        } else {
          this.report = null;
        }
      } catch (e) {
        this.$helpers.message("Error", e.message, "error");
        this.view = "list";
      }
      this.loading = false;
    },
    async buildTableRows() {
      const items = (this.current && this.current.Items) || [];
      // Si no tenemos nombres de producto en items, los buscamos en el catálogo.
      if (this.catalog.length === 0) {
        await this.loadCatalog();
      }
      const nameById = {};
      for (const p of this.catalog) {
        nameById[p.Id] = p.Name;
      }
      this.tableRows = items.map((i) => ({
        Id: i.Id,
        ProductId: i.ProductId,
        Name: nameById[i.ProductId] || `Producto ${i.ProductId}`,
        SystemStock: Number(i.SystemStock),
        PhysicalStock: i.PhysicalStock !== null && i.PhysicalStock !== undefined
          ? Number(i.PhysicalStock)
          : null,
        PhysicalStockEdit:
          i.PhysicalStock !== null && i.PhysicalStock !== undefined
            ? Number(i.PhysicalStock)
            : "",
        Notes: i.Notes || "",
        NotesEdit: i.Notes || "",
      }));
    },
    backToList() {
      this.view = "list";
      this.loadList();
    },
    collectChanges() {
      // Solo enviamos filas con valor capturado.
      return this.tableRows
        .filter(
          (r) =>
            r.PhysicalStockEdit !== "" &&
            r.PhysicalStockEdit !== null &&
            r.PhysicalStockEdit !== undefined,
        )
        .map((r) => ({
          ItemId: r.Id,
          PhysicalStock: Number(r.PhysicalStockEdit),
          Notes: r.NotesEdit || "",
        }));
    },
    async saveDraft() {
      const items = this.collectChanges();
      if (items.length === 0) {
        this.$helpers.toast("No hay valores capturados", "warning");
        return;
      }
      this.saving = true;
      try {
        await updateReconciliationItems(this.current.Id, items);
        this.$helpers.toast("Avance guardado");
        await this.openDetail(this.current.Id);
      } catch (e) {
        this.$helpers.message("Error", e.message, "error");
      }
      this.saving = false;
    },
    async closeRecon() {
      const ok = await this.$helpers.confirm(
        "Al cerrar se aplicarán los ajustes al stock. Esta acción no se puede deshacer.",
      );
      if (!ok) return;
      this.saving = true;
      try {
        const items = this.collectChanges();
        if (items.length > 0) {
          await updateReconciliationItems(this.current.Id, items);
        }
        await closeReconciliation(this.current.Id);
        this.$helpers.toast("Conciliación cerrada");
        await this.openDetail(this.current.Id);
      } catch (e) {
        this.$helpers.message("Error", e.message, "error");
      }
      this.saving = false;
    },
    async cancelRecon() {
      const ok = await this.$helpers.confirm(
        "¿Cancelar esta conciliación? No se aplicarán los ajustes.",
      );
      if (!ok) return;
      this.saving = true;
      try {
        await cancelReconciliation(this.current.Id);
        this.$helpers.toast("Conciliación cancelada");
        await this.openDetail(this.current.Id);
      } catch (e) {
        this.$helpers.message("Error", e.message, "error");
      }
      this.saving = false;
    },
    downloadExcel() {
      const rows = this.tableRows.map((r) => ({
        Producto: r.Name,
        "Stock sistema": r.SystemStock,
        "Stock físico":
          r.PhysicalStock !== null && r.PhysicalStock !== undefined
            ? r.PhysicalStock
            : "",
        Diferencia: this.diffText(r),
        Notas: r.Notes || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Conciliación");
      XLSX.writeFile(wb, `Conciliacion_${this.current.Id}.xlsx`);
    },
  },
  beforeMount() {
    this.loadList();
  },
};
</script>

<style scoped>
.bold {
  font-weight: 600;
}
</style>
